import React, { useMemo, useState } from 'react';
import {
  MagnifyingGlassIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { getGroupedNodeTypes } from './editorUtils';
import NodeIcon from './NodeIcon';

const CategoryIcon = ({ icon, className = 'w-6 h-6' }) => {
  switch (icon) {
    case 'ai':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /></svg>;
    case 'globe':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg>;
    case 'transform':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>;
    case 'flow':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" /></svg>;
    case 'core':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /></svg>;
    case 'trigger':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>;
    case 'communication':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>;
    case 'development':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M16 18l6-6-6-6M8 6l-6 6 6 6" /></svg>;
    case 'database':
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>;
    default:
      return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></svg>;
  }
};

const NodePalette = ({ nodeTypes }) => {
  const groups = useMemo(() => getGroupedNodeTypes(nodeTypes), [nodeTypes]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);

  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify({ nodeType }));
    event.dataTransfer.effectAllowed = 'move';
  };

  const query = search.toLowerCase().trim();
  const isSearching = query.length > 0;

  const searchResults = useMemo(() => (
    isSearching
      ? nodeTypes.filter((t) =>
        t.label.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query) ||
        t.key.toLowerCase().includes(query)
      )
      : []
  ), [isSearching, nodeTypes, query]);

  const activeCategoryGroup = activeCategory ? groups[activeCategory] : null;

  return (
    <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col" style={{ width: '296px' }}>
      {/* Search */}
      <div className="p-4 flex-shrink-0">
        {activeCategory && !isSearching && (
          <button
            onClick={() => setActiveCategory(null)}
            className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-3 transition-colors"
          >
            <ArrowLeftIcon className="w-3.5 h-3.5" />
            Back
          </button>
        )}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search nodes..."
            className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:bg-white dark:focus:bg-gray-800"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto border-t border-violet-800/50 py-4">
        {/* Search results */}
        {isSearching && (
          <div className="px-4 py-0 flex flex-col gap-2 clearfix h-auto">
            {searchResults.length === 0 && (
              <div className="text-center py-8 text-sm text-gray-400">
                No nodes match &ldquo;{search}&rdquo;
              </div>
            )}
            {searchResults.map((nodeType) => (
              <div
                key={nodeType.key}
                draggable
                onDragStart={(e) => onDragStart(e, nodeType.key)}
                className="flex items-start gap-3 cursor-grab hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-md px-1 py-2 -mx-1 transition-colors active:cursor-grabbing"
              >
                <span className="flex-shrink-0 w-8 h-8 rounded-md bg-violet-700 flex items-center justify-center mt-0.5">
                  <NodeIcon nodeKey={nodeType.key} label={nodeType.label} size="sm" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                    {nodeType.label}
                  </div>
                  {nodeType.description && nodeType.description !== nodeType.label && (
                    <div className="text-xs text-gray-400 dark:text-gray-500 line-clamp-2">
                      {nodeType.description}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Category list */}
        {!isSearching && !activeCategory && (
          <div className="px-3 pb-3 space-y-4">
            {Object.entries(groups).map(([catKey, group]) => (
              <button
                key={catKey}
                onClick={() => setActiveCategory(catKey)}
                className="w-full flex items-center gap-3 px-3 py-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
              >
                <span className="flex-shrink-0 text-gray-400 dark:text-gray-500">
                  <CategoryIcon icon={group.icon} />
                </span>
                <div className="min-w-0 flex-1 overflow-hidden">
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                    {group.label}
                  </div>
                  {group.description && (
                    <div className="text-xs text-gray-400 dark:text-gray-500 truncate">
                      {group.description}
                    </div>
                  )}
                </div>
                <ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-gray-300 dark:text-gray-600" />
              </button>
            ))}
          </div>
        )}

        {/* Category detail — node list */}
        {!isSearching && activeCategoryGroup && (
          <div className="px-4 py-0 flex flex-col gap-2 clearfix h-auto">
            {activeCategoryGroup.types.map((nodeType) => (
              <div
                key={nodeType.key}
                draggable
                onDragStart={(e) => onDragStart(e, nodeType.key)}
                className="clearfix h-auto flex items-start gap-3 cursor-grab hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-md px-1 py-2 -mx-1 transition-colors active:cursor-grabbing"
              >
                <span className="flex-shrink-0 w-8 h-8 rounded-md bg-violet-700 flex items-center justify-center mt-0.5">
                  <NodeIcon nodeKey={nodeType.key} label={nodeType.label} size="sm" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                    {nodeType.label}
                  </div>
                  {nodeType.description && nodeType.description !== nodeType.label && (
                    <div className="text-xs text-gray-400 dark:text-gray-500 line-clamp-2">
                      {nodeType.description}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NodePalette;
